import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Save, ArrowLeft, Baby, Eye, MessageCircle, FileText } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { infantilAPI } from '../services/api';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

export default function NewObservationPage() {
  const navigate = useNavigate();
  const [ref, setRef] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    student_id: '', class_id: '', date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5), trimester: '1º Trimestre',
    context: '', evidence_type: 'escrita', title: '', observation_text: '',
    child_speech: '', bncc_field_id: '', bncc_objective_id: '', dimension_id: '',
    evidence_strength: 'evidência', evidence_nature: 'espontânea',
    teacher_interpretation: '', perceived_advance: '', next_steps: '',
    publish_to_portfolio: false, publish_to_family: false, media_urls: [],
  });

  useEffect(() => {
    infantilAPI.reference().then(setRef);
    infantilAPI.classes().then(setClasses);
  }, []);

  useEffect(() => {
    if (form.class_id) infantilAPI.classStudents(form.class_id).then(setStudents);
  }, [form.class_id]);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const filteredObjectives = ref?.bncc_objectives?.filter(o => !form.bncc_field_id || o.field_id === form.bncc_field_id) || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_id || !form.class_id || !form.title || !form.observation_text || !form.context) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await infantilAPI.observations.create({ ...form, teacher_id: user.id, teacher_name: user.name });
      toast.success('Observação registrada com sucesso!');
      navigate('/v2/infantil/observacoes');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const Select = ({ label, value, onChange, children, required }) => (
    <div>
      <Label className="text-xs text-slate-600">{label}{required && ' *'}</Label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-[#64327D]/20 focus:border-[#64327D] transition-all">
        <option value="">Selecione...</option>
        {children}
      </select>
    </div>
  );

  return (
    <motion.div initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
      className="max-w-4xl mx-auto space-y-6" data-testid="new-observation-page">
      {/* Header */}
      <motion.div variants={fadeUp} className="flex items-center gap-3">
        <button onClick={() => navigate('/v2/infantil/observacoes')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
          <ArrowLeft className="h-5 w-5 text-slate-500" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#2BB673]" /> Novo Registro de Observação
          </h1>
          <p className="text-sm text-slate-500">Documente uma evidência pedagógica</p>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Contexto */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-sm font-bold text-[#2E1A47] mb-4 flex items-center gap-2">
            <Baby className="h-4 w-4 text-[#64327D]" /> Identificação
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select label="Turma" value={form.class_id} onChange={v => set('class_id', v)} required>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Select label="Criança" value={form.student_id} onChange={v => set('student_id', v)} required>
              {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <Select label="Contexto" value={form.context} onChange={v => set('context', v)} required>
              {(ref?.contexts || []).map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div>
              <Label className="text-xs text-slate-600">Data *</Label>
              <Input type="date" value={form.date} onChange={e => set('date', e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Hora</Label>
              <Input type="time" value={form.time} onChange={e => set('time', e.target.value)} className="rounded-xl" />
            </div>
            <Select label="Tipo de Evidência" value={form.evidence_type} onChange={v => set('evidence_type', v)}>
              {(ref?.evidence_types || []).map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
            <Select label="Trimestre" value={form.trimester} onChange={v => set('trimester', v)}>
              {['1º Trimestre', '2º Trimestre', '3º Trimestre'].map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
        </motion.div>

        {/* Section 2: Observação */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-sm font-bold text-[#2E1A47] mb-4 flex items-center gap-2">
            <Eye className="h-4 w-4 text-[#64327D]" /> Registro da Observação
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">Título curto *</Label>
              <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Ex: Construção com blocos em grupo" className="rounded-xl" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Observação livre *</Label>
              <textarea value={form.observation_text} onChange={e => set('observation_text', e.target.value)}
                placeholder="Descreva o que você observou. Seja detalhista e descritiva, focando no processo e não no resultado..."
                className="w-full h-32 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-[#64327D]/20 focus:border-[#64327D]" />
            </div>
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><MessageCircle className="h-3 w-3" /> Fala da criança</Label>
              <Input value={form.child_speech} onChange={e => set('child_speech', e.target.value)} placeholder="Registre a fala exata da criança..." className="rounded-xl italic" />
            </div>
          </div>
        </motion.div>

        {/* Section 3: Classificação Pedagógica */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-sm font-bold text-[#2E1A47] mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#64327D]" /> Classificação Pedagógica
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Campo BNCC" value={form.bncc_field_id} onChange={v => { set('bncc_field_id', v); set('bncc_objective_id', ''); }}>
              {(ref?.bncc_fields || []).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </Select>
            <Select label="Objetivo BNCC" value={form.bncc_objective_id} onChange={v => set('bncc_objective_id', v)}>
              {filteredObjectives.map(o => <option key={o.id} value={o.id}>{o.code} — {o.description?.substring(0, 60)}...</option>)}
            </Select>
            <Select label="Dimensão Observada" value={form.dimension_id} onChange={v => set('dimension_id', v)}>
              {(ref?.dimensions || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Select>
            <Select label="Força da Evidência" value={form.evidence_strength} onChange={v => set('evidence_strength', v)}>
              {(ref?.evidence_strengths || []).map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Select label="Natureza da Evidência" value={form.evidence_nature} onChange={v => set('evidence_nature', v)}>
              {(ref?.evidence_natures || []).map(n => <option key={n} value={n}>{n}</option>)}
            </Select>
          </div>
        </motion.div>

        {/* Section 4: Leitura do Professor */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-sm font-bold text-[#2E1A47] mb-4">Leitura do Professor</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">Interpretação pedagógica</Label>
              <textarea value={form.teacher_interpretation} onChange={e => set('teacher_interpretation', e.target.value)}
                placeholder="O que essa cena revela sobre o desenvolvimento da criança?"
                className="w-full h-20 rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-[#64327D]/20" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-600">Avanço percebido</Label>
                <Input value={form.perceived_advance} onChange={e => set('perceived_advance', e.target.value)} placeholder="O que a criança está consolidando?" className="rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Próximos passos</Label>
                <Input value={form.next_steps} onChange={e => set('next_steps', e.target.value)} placeholder="Que propostas posso fazer a seguir?" className="rounded-xl" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Submit */}
        <motion.div variants={fadeUp} className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/v2/infantil/observacoes')} className="rounded-xl">Cancelar</Button>
          <Button type="submit" disabled={saving} className="rounded-xl px-6" data-testid="save-observation-btn"
            style={{ background: 'linear-gradient(135deg, #2BB673, #1a8f5a)' }}>
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar Observação'}
          </Button>
        </motion.div>
      </form>
    </motion.div>
  );
}
